// ---------- la progression, sur l'appareil ----------
// Par partie : les lettres posées, les indices pris, les lignes révélées, et si elle est finie
// (avec ses étoiles). Par chapitre : les parties passées. Et le mot du jour : la série en cours.
//
// Indexé par l'IDENTIFIANT des parties et des chapitres, jamais par leur place : un chapitre
// ajouté côté serveur ne décale rien. C'est aussi pourquoi l'atelier refuse de déplacer un
// chapitre publié ou de toucher aux lignes d'une partie publiée (backend/src/routes/admin-content.ts).
//
// `:v2` : la v1 était celle des mots fléchés, abandonnés. Rien à reprendre — le jeu n'a jamais été
// publié sous cette forme —, donc on n'y relit rien.
//
// LE CACHE IMMÉDIAT D'UNE COPIE EN BASE. Ce fichier reste la seule chose que le jeu lit : il
// marche hors ligne et sans compte, exactement comme avant. src/sync.ts recopie ce qui change vers
// le compte du joueur (backend/src/routes/account.ts) et ramène ce qu'un autre appareil y a mis ;
// la règle qui réunit deux sauvegardes est dans shared/save.ts, partagée avec le Worker.
//
// TOUJOURS `:v2` malgré les deux champs ajoutés aux parties (`n`, `t`, cf. shared/save.ts). Ils
// sont facultatifs et leur absence a un sens (génération 0, jamais horodatée) : une sauvegarde
// écrite avant eux se relit telle quelle. C'est la règle du format de `GET /content` (CLAUDE.md) —
// un champ AJOUTÉ ne fait pas monter le numéro, un champ qui change de sens, si.

import {
  canon,
  isBlank,
  mergePuzzle,
  mergeSkipped,
  mergeStreak,
  samePuzzle,
  type DailyStreak,
  type PuzzleSave,
  type SaveState,
} from '../shared/save';

const KEY = 'verbigrid:progress:v2';

/** La forme est celle de shared/save.ts : le Worker stocke la même, champ pour champ. */
export type PuzzleProgress = PuzzleSave;

interface Saved extends SaveState {
  /** le chapitre que l'accueil montrait en dernier — propre à CET appareil, il ne voyage pas :
   *  ramené d'un autre téléphone, il ferait sauter l'accueil d'un chapitre à l'autre sous les
   *  yeux du joueur, et `frontier` (src/quest.ts) sait déjà retrouver où il en est. */
  chapter?: string;
}

let saved: Saved = { puzzles: {}, skipped: {} };
try {
  const raw = localStorage.getItem(KEY);
  if (raw) {
    const p = JSON.parse(raw) as Partial<Saved>;
    saved = {
      puzzles: p.puzzles ?? {},
      skipped: p.skipped ?? {},
      chapter: p.chapter,
      streak: p.streak,
    };
  }
} catch {
  /* première visite, ou stockage refusé : on part de zéro */
}

// ---------- ce qui a changé depuis la dernière écriture ----------
// play.ts modifie la partie EN PLACE (il tient l'objet rendu par `progress()`) puis appelle
// `persist()`, sans dire ce qu'il a touché. Plutôt que de faire passer chaque frappe par une
// fonction de plus — et de toucher à tous les appelants —, `persist()` compare chaque entrée à
// son dernier état connu : c'est ce qui horodate une partie (`t`) et ce qui dit à src/sync.ts quoi
// envoyer. Le coût est un JSON par partie à chaque écriture, quelques dizaines de microsecondes
// pour cent parties : rien à côté du localStorage.setItem qui suit, lui-même un JSON de tout.

/** Le dernier état écrit de chaque entrée, SANS son horodatage (qui en dépend). */
const seen = new Map<string, string>();
const puzzleKey = (p: PuzzleSave): string => canon({ ...p, t: undefined });

function remember(): void {
  for (const [id, p] of Object.entries(saved.puzzles)) seen.set('p:' + id, puzzleKey(p));
  for (const [id, l] of Object.entries(saved.skipped)) seen.set('s:' + id, canon(l));
  seen.set('streak', canon(saved.streak));
}
remember();

/** Ce qui a bougé, par identifiant : src/sync.ts relit l'état au moment d'envoyer, pas celui-ci. */
export interface Changes {
  puzzles: string[];
  chapters: string[];
  streak: boolean;
}

let onChange: ((c: Changes) => void) | null = null;

/** Branché par src/sync.ts. Sans lui (aucune synchro démarrée), rien ne change au comportement. */
export function watchSaves(cb: (c: Changes) => void): void {
  onChange = cb;
}

function diff(): Changes {
  const out: Changes = { puzzles: [], chapters: [], streak: false };
  for (const [id, p] of Object.entries(saved.puzzles)) {
    const k = puzzleKey(p);
    const before = seen.get('p:' + id);
    if (k === before) continue;
    seen.set('p:' + id, k);
    // Une partie que `progress()` vient de créer vide, en la lisant, n'est pas un coup joué : ni
    // horodatée ni envoyée. Horodatée, elle passerait pour plus récente que la vraie partie qu'un
    // autre appareil a jouée et qui n'est pas encore arrivée ici — et l'effacerait.
    if (before === undefined && isBlank(p)) continue;
    p.t = Date.now();
    out.puzzles.push(id);
  }
  for (const [id, l] of Object.entries(saved.skipped)) {
    const k = canon(l);
    if (seen.get('s:' + id) === k) continue;
    seen.set('s:' + id, k);
    out.chapters.push(id);
  }
  const s = canon(saved.streak);
  if (seen.get('streak') !== s) {
    seen.set('streak', s);
    out.streak = true;
  }
  return out;
}

function write(): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(saved));
  } catch {
    /* stockage plein ou refusé : la partie continue, sans sauvegarde */
  }
}

export function persist(): void {
  const c = diff();
  write();
  if (onChange && (c.puzzles.length || c.chapters.length || c.streak)) onChange(c);
}

export const progress = (id: string): PuzzleProgress =>
  (saved.puzzles[id] ||= { e: {}, h: {}, r: {}, g: {} });
export const isDone = (id: string): boolean => saved.puzzles[id]?.done !== undefined;
/** Le nombre de parties finies, tous chapitres et mots du jour confondus. */
export const doneTotal = (): number =>
  Object.values(saved.puzzles).filter((p) => p.done !== undefined).length;
export const starsOf = (id: string): number => saved.puzzles[id]?.done ?? 0;
export const mysteryOf = (id: string): string | undefined => saved.puzzles[id]?.w;

/** Une partie se recommence de zéro (« Rejouer ») : ses étoiles restent acquises, on ne perd
 *  jamais ce qu'on a gagné. La génération monte (`n`) : c'est elle qui fera gagner ce plateau vide
 *  contre le plateau plein qu'un autre appareil, ou la base, garde encore (shared/save.ts). */
export function restart(id: string): void {
  const old = saved.puzzles[id];
  const kept = old?.done;
  const w = old?.w;
  saved.puzzles[id] = {
    e: {},
    h: {},
    r: {},
    g: {},
    ...(kept !== undefined ? { done: kept, w } : {}),
    n: (old?.n ?? 0) + 1,
  };
  persist();
}

/** Trois étoiles sans aide, deux avec un indice, une au-delà. Une ligne révélée compte double :
 *  elle donne toutes ses lettres d'un coup, là où un indice n'en donne aucune. */
export function helpStars(p: PuzzleProgress): number {
  const help = Object.values(p.h).reduce((a, b) => a + b, 0) + 2 * Object.keys(p.r).length;
  return help === 0 ? 3 : help === 1 ? 2 : 1;
}

/** Marque la partie finie et rend les étoiles de CETTE fois. Rejouer mieux améliore le score
 *  gardé ; rejouer moins bien ne le dégrade pas. */
export function finish(id: string, mystery: string): number {
  const p = progress(id);
  const s = helpStars(p);
  p.done = Math.max(p.done ?? 0, s);
  p.w = mystery;
  persist();
  return s;
}

export const skippedIn = (chapterId: string): string[] => saved.skipped[chapterId] ?? [];
export function skip(chapterId: string, puzzleId: string): void {
  const list = (saved.skipped[chapterId] ||= []);
  if (!list.includes(puzzleId)) list.push(puzzleId);
  persist();
}

export const lastChapter = (): string | undefined => saved.chapter;
export function rememberChapter(id: string): void {
  saved.chapter = id;
  persist();
}

const dayMs = 86_400_000;
const todayUtc = (): string => new Date().toISOString().slice(0, 10);

/** La série du mot du jour, vue aujourd'hui : elle tient tant que le dernier jour trouvé est
 *  aujourd'hui ou hier (en UTC, comme le tirage côté serveur). */
export function streak(): number {
  const s = saved.streak;
  if (!s) return 0;
  return (Date.parse(todayUtc()) - Date.parse(s.last)) / dayMs <= 1 ? s.count : 0;
}

export function dailySolved(day: string): void {
  const s = saved.streak;
  // `<=` et non `===` : un mot du jour fini APRÈS minuit (ouvert la veille), ou une sauvegarde
  // d'un autre appareil déjà plus avancée, ne doit pas faire reculer la série à 1. Les dates sont
  // au format AAAA-MM-JJ, qui se compare comme du texte.
  if (s && day <= s.last) return;
  const gap = s ? (Date.parse(day) - Date.parse(s.last)) / dayMs : Infinity;
  saved.streak = { last: day, count: gap === 1 ? s!.count + 1 : 1 };
  persist();
}

// ---------- l'échange avec le compte (src/sync.ts) ----------

/** Ce qui part vers le serveur. Sans argument : tout ce qui vaut d'être envoyé (les parties vides
 *  restent ici, elles n'apprendraient rien à personne). */
export function localSave(changes?: Changes): SaveState {
  const ids = changes?.puzzles ?? Object.keys(saved.puzzles);
  const chs = changes?.chapters ?? Object.keys(saved.skipped);
  const puzzles: Record<string, PuzzleSave> = {};
  for (const id of ids) {
    const p = saved.puzzles[id];
    if (p && !isBlank(p)) puzzles[id] = p;
  }
  const skipped: Record<string, string[]> = {};
  for (const id of chs) if (saved.skipped[id]) skipped[id] = saved.skipped[id];
  const streak = !changes || changes.streak ? saved.streak : undefined;
  return { puzzles, skipped, ...(streak ? { streak } : {}) };
}

/**
 * Une sauvegarde venue du serveur, réunie à celle de l'appareil. Rend deux choses : si l'écran a
 * quelque chose de neuf à montrer, et ce que l'appareil sait de PLUS que le serveur — ce qu'il
 * faut donc lui renvoyer (une partie jouée hors ligne, une sauvegarde d'avant le compte).
 *
 * EN PLACE, et ce n'est pas un détail : play.ts garde l'objet de la partie ouverte et l'écrit
 * directement. Remplacer cet objet par un neuf laisserait le jeu écrire dans un objet que plus
 * personne ne sauvegarde. Seul `restart` le remplace, et il le fait avant l'ouverture.
 *
 * Rien ici ne passe par `onChange` : ce qui arrive du serveur n'a pas à y repartir. `seen` est mis
 * à jour d'abord, donc le prochain `persist()` ne le prend pas pour un coup joué — il l'horodaterait
 * à maintenant, et ce plateau gagnerait à tort contre un plus récent ailleurs.
 */
export function adoptSave(remote: SaveState): { changed: boolean; ahead: Changes } {
  const ahead: Changes = { puzzles: [], chapters: [], streak: false };
  let changed = false;
  const ids = new Set([...Object.keys(remote.puzzles), ...Object.keys(saved.puzzles)]);
  for (const id of ids) {
    const mine = saved.puzzles[id];
    const theirs = remote.puzzles[id];
    if (!theirs) {
      if (mine && !isBlank(mine)) ahead.puzzles.push(id);
      continue;
    }
    const merged = mergePuzzle(mine, theirs);
    if (!samePuzzle(merged, mine)) {
      changed = true;
      if (mine) {
        for (const k of Object.keys(mine) as (keyof PuzzleSave)[]) delete mine[k];
        Object.assign(mine, merged);
      } else saved.puzzles[id] = merged;
      seen.set('p:' + id, puzzleKey(saved.puzzles[id]));
    }
    if (!samePuzzle(merged, theirs)) ahead.puzzles.push(id);
  }
  const chs = new Set([...Object.keys(remote.skipped), ...Object.keys(saved.skipped)]);
  for (const id of chs) {
    const merged = mergeSkipped(saved.skipped[id], remote.skipped[id]);
    if (canon(merged) !== canon(saved.skipped[id] ?? [])) {
      changed = true;
      // en place aussi : `skip()` pousse dans ce tableau
      const list = (saved.skipped[id] ||= []);
      list.splice(0, list.length, ...merged);
      seen.set('s:' + id, canon(list));
    }
    if (canon(merged) !== canon(remote.skipped[id] ?? [])) ahead.chapters.push(id);
  }
  const s: DailyStreak | undefined = mergeStreak(saved.streak, remote.streak);
  if (canon(s) !== canon(saved.streak)) {
    changed = true;
    saved.streak = s;
    seen.set('streak', canon(s));
  }
  if (canon(s) !== canon(remote.streak)) ahead.streak = true;
  if (changed) write();
  return { changed, ahead };
}
